/**
 * Chain of Custody Tracking Service
 *
 * Provides complete movement history, handover verification,
 * transit tracking, and custody analytics for personnel files.
 */

export interface CustodyRecord {
  id: string;
  pfileId: string;
  fileNumber: string;
  staffName: string;
  movementType: CustodyMovementType;
  fromLocation: CustodyLocation;
  toLocation: CustodyLocation;
  fromCustodian: CustodianInfo;
  toCustodian: CustodianInfo;
  reason: string;
  timestamp: Date;
  verificationMethod?: VerificationMethod;
  verifiedAt?: Date;
  signature?: string;
  witnessName?: string;
  transitStatus?: TransitStatus;
  expectedArrival?: Date;
  actualArrival?: Date;
  notes?: string;
  attachments?: string[];
}

export type CustodyMovementType =
  | 'checkout'          // File taken from registry
  | 'checkin'           // File returned to registry
  | 'transfer'          // Custody transferred to another person
  | 'inter_mda_transfer' // Transfer between MDAs
  | 'location_change'   // Physical location changed
  | 'temporary_access'  // Short-term access (viewing only)
  | 'retrieval'         // Retrieved from archive
  | 'archival'          // Sent to archive
  | 'disposal';         // Certified destruction

export interface CustodyLocation {
  type: 'registry' | 'office' | 'transit' | 'archive' | 'external';
  mda: string;
  building?: string;
  room?: string;
  cabinet?: string;
  shelf?: string;
  description?: string;
}

export interface CustodianInfo {
  id: string;
  name: string;
  role: string;
  department: string;
  mda: string;
  contactPhone?: string;
  contactEmail?: string;
}

export type VerificationMethod =
  | 'signature'         // Physical signature
  | 'biometric'         // Fingerprint/facial recognition
  | 'digital_signature' // Electronic signature
  | 'otp'               // One-time password verification
  | 'witness'           // Witnessed handover
  | 'system';           // System-verified (auto check-in)

export type TransitStatus =
  | 'preparing'         // Being prepared for transfer
  | 'dispatched'        // Sent out
  | 'in_transit'        // Currently moving between locations
  | 'arrived'           // Reached destination
  | 'received'          // Officially received and acknowledged
  | 'delayed'           // Delayed in transit
  | 'lost';             // Lost in transit (escalation required)

export interface CustodyChain {
  pfileId: string;
  fileNumber: string;
  staffName: string;
  currentLocation: CustodyLocation;
  currentCustodian: CustodianInfo | null;
  isCheckedOut: boolean;
  totalMovements: number;
  averageCustodyDuration: number; // in days
  longestCustodyDuration: number; // in days
  chain: CustodyRecord[];
  alerts: CustodyAlert[];
}

export interface CustodyAlert {
  id: string;
  type: CustodyAlertType;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  timestamp: Date;
  isResolved: boolean;
  resolvedBy?: string;
  resolvedAt?: Date;
  resolution?: string;
}

export type CustodyAlertType =
  | 'overdue_return'       // File not returned by expected date
  | 'extended_custody'     // Custody duration exceeding threshold
  | 'unauthorized_access'  // Access attempt without permission
  | 'location_mismatch'    // Physical location doesn't match records
  | 'transit_delay'        // Inter-MDA transfer delayed
  | 'missing_verification' // Handover not verified
  | 'chain_break';         // Gap in custody chain

export interface CustodyAnalytics {
  period: { start: Date; end: Date };
  totalMovements: number;
  checkouts: number;
  checkins: number;
  transfers: number;
  interMdaTransfers: number;
  averageCheckoutDuration: number;
  overdueFiles: number;
  topRequesters: { name: string; count: number }[];
  movementsByDay: { date: string; count: number }[];
  movementsByType: Record<CustodyMovementType, number>;
  alerts: {
    total: number;
    unresolved: number;
    bySeverity: { info: number; warning: number; critical: number };
  };
}

class CustodyTrackingService {
  /**
   * Create a new custody record
   */
  createCustodyRecord(params: {
    pfileId: string;
    fileNumber: string;
    staffName: string;
    movementType: CustodyMovementType;
    fromLocation: CustodyLocation;
    toLocation: CustodyLocation;
    fromCustodian: CustodianInfo;
    toCustodian: CustodianInfo;
    reason: string;
    verificationMethod?: VerificationMethod;
    notes?: string;
  }): CustodyRecord {
    return {
      id: `custody_${Date.now()}_${params.pfileId}`,
      pfileId: params.pfileId,
      fileNumber: params.fileNumber,
      staffName: params.staffName,
      movementType: params.movementType,
      fromLocation: params.fromLocation,
      toLocation: params.toLocation,
      fromCustodian: params.fromCustodian,
      toCustodian: params.toCustodian,
      reason: params.reason,
      timestamp: new Date(),
      verificationMethod: params.verificationMethod,
      verifiedAt: params.verificationMethod ? new Date() : undefined,
      notes: params.notes,
    };
  }

  /**
   * Build complete custody chain for a PFile
   */
  buildCustodyChain(
    pfileId: string,
    fileNumber: string,
    staffName: string,
    records: CustodyRecord[]
  ): CustodyChain {
    const sortedRecords = [...records].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Get current state from last record
    const lastRecord = sortedRecords[sortedRecords.length - 1];
    const currentLocation = lastRecord?.toLocation || {
      type: 'registry' as const,
      mda: 'OHCS',
      description: 'Main Registry',
    };
    const currentCustodian = lastRecord?.toCustodian || null;
    const isCheckedOut = lastRecord?.movementType === 'checkout' ||
                         lastRecord?.movementType === 'transfer';

    // Calculate custody durations
    const durations: number[] = [];
    for (let i = 0; i < sortedRecords.length - 1; i++) {
      const start = new Date(sortedRecords[i].timestamp);
      const end = new Date(sortedRecords[i + 1].timestamp);
      const days = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
      durations.push(days);
    }

    const averageCustodyDuration = durations.length > 0
      ? durations.reduce((a, b) => a + b, 0) / durations.length
      : 0;
    const longestCustodyDuration = durations.length > 0
      ? Math.max(...durations)
      : 0;

    // Generate alerts
    const alerts = this.generateAlerts(sortedRecords, isCheckedOut, lastRecord);

    return {
      pfileId,
      fileNumber,
      staffName,
      currentLocation,
      currentCustodian,
      isCheckedOut,
      totalMovements: sortedRecords.length,
      averageCustodyDuration: Math.round(averageCustodyDuration * 10) / 10,
      longestCustodyDuration: Math.round(longestCustodyDuration * 10) / 10,
      chain: sortedRecords,
      alerts,
    };
  }

  /**
   * Generate custody alerts
   */
  private generateAlerts(
    records: CustodyRecord[],
    isCheckedOut: boolean,
    lastRecord?: CustodyRecord
  ): CustodyAlert[] {
    const alerts: CustodyAlert[] = [];
    const now = new Date();

    // Check for overdue return
    if (isCheckedOut && lastRecord) {
      const checkoutDate = new Date(lastRecord.timestamp);
      const daysSinceCheckout = (now.getTime() - checkoutDate.getTime()) / (1000 * 60 * 60 * 24);

      if (daysSinceCheckout > 30) {
        alerts.push({
          id: `alert_overdue_${lastRecord.id}`,
          type: 'overdue_return',
          severity: daysSinceCheckout > 60 ? 'critical' : 'warning',
          message: `File checked out for ${Math.round(daysSinceCheckout)} days without return`,
          timestamp: now,
          isResolved: false,
        });
      } else if (daysSinceCheckout > 14) {
        alerts.push({
          id: `alert_extended_${lastRecord.id}`,
          type: 'extended_custody',
          severity: 'info',
          message: `File has been out for ${Math.round(daysSinceCheckout)} days`,
          timestamp: now,
          isResolved: false,
        });
      }
    }

    // Check for missing verification in recent records
    const recentUnverified = records
      .filter(r => !r.verifiedAt && (now.getTime() - new Date(r.timestamp).getTime()) < 7 * 24 * 60 * 60 * 1000);

    if (recentUnverified.length > 0) {
      alerts.push({
        id: `alert_unverified_${Date.now()}`,
        type: 'missing_verification',
        severity: 'warning',
        message: `${recentUnverified.length} recent movement(s) pending verification`,
        timestamp: now,
        isResolved: false,
      });
    }

    // Check for transit delays
    const inTransit = records.filter(r =>
      r.transitStatus === 'in_transit' ||
      r.transitStatus === 'dispatched'
    );

    inTransit.forEach(record => {
      if (record.expectedArrival && new Date(record.expectedArrival) < now) {
        alerts.push({
          id: `alert_transit_${record.id}`,
          type: 'transit_delay',
          severity: 'warning',
          message: `Inter-MDA transfer delayed - expected arrival was ${new Date(record.expectedArrival).toLocaleDateString()}`,
          timestamp: now,
          isResolved: false,
        });
      }
    });

    return alerts;
  }

  /**
   * Generate custody analytics
   */
  generateAnalytics(
    records: CustodyRecord[],
    startDate: Date,
    endDate: Date
  ): CustodyAnalytics {
    const periodRecords = records.filter(r => {
      const timestamp = new Date(r.timestamp);
      return timestamp >= startDate && timestamp <= endDate;
    });

    // Count by movement type
    const movementsByType: Record<CustodyMovementType, number> = {
      checkout: 0,
      checkin: 0,
      transfer: 0,
      inter_mda_transfer: 0,
      location_change: 0,
      temporary_access: 0,
      retrieval: 0,
      archival: 0,
      disposal: 0,
    };

    periodRecords.forEach(r => {
      movementsByType[r.movementType]++;
    });

    // Calculate checkout durations
    const checkoutDurations: number[] = [];
    const checkouts = periodRecords.filter(r => r.movementType === 'checkout');
    checkouts.forEach(checkout => {
      const checkin = periodRecords.find(r =>
        r.pfileId === checkout.pfileId &&
        r.movementType === 'checkin' &&
        new Date(r.timestamp) > new Date(checkout.timestamp)
      );
      if (checkin) {
        const duration = (new Date(checkin.timestamp).getTime() - new Date(checkout.timestamp).getTime())
          / (1000 * 60 * 60 * 24);
        checkoutDurations.push(duration);
      }
    });

    // Top requesters
    const requesterCounts = new Map<string, number>();
    periodRecords
      .filter(r => r.movementType === 'checkout')
      .forEach(r => {
        const name = r.toCustodian.name;
        requesterCounts.set(name, (requesterCounts.get(name) || 0) + 1);
      });

    const topRequesters = Array.from(requesterCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Movements by day
    const dayMap = new Map<string, number>();
    periodRecords.forEach(r => {
      const day = new Date(r.timestamp).toISOString().split('T')[0];
      dayMap.set(day, (dayMap.get(day) || 0) + 1);
    });

    const movementsByDay = Array.from(dayMap.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Count alerts
    const now = new Date();
    const overdueFiles = checkouts.filter(checkout => {
      const checkin = periodRecords.find(r =>
        r.pfileId === checkout.pfileId &&
        r.movementType === 'checkin' &&
        new Date(r.timestamp) > new Date(checkout.timestamp)
      );
      if (!checkin) {
        const daysSince = (now.getTime() - new Date(checkout.timestamp).getTime()) / (1000 * 60 * 60 * 24);
        return daysSince > 30;
      }
      return false;
    }).length;

    return {
      period: { start: startDate, end: endDate },
      totalMovements: periodRecords.length,
      checkouts: movementsByType.checkout,
      checkins: movementsByType.checkin,
      transfers: movementsByType.transfer,
      interMdaTransfers: movementsByType.inter_mda_transfer,
      averageCheckoutDuration: checkoutDurations.length > 0
        ? Math.round(checkoutDurations.reduce((a, b) => a + b, 0) / checkoutDurations.length * 10) / 10
        : 0,
      overdueFiles,
      topRequesters,
      movementsByDay,
      movementsByType,
      alerts: {
        total: periodRecords.filter(r => !r.verifiedAt).length,
        unresolved: overdueFiles,
        bySeverity: {
          info: 0,
          warning: overdueFiles,
          critical: 0,
        },
      },
    };
  }

  /**
   * Get movement type label
   */
  getMovementTypeLabel(type: CustodyMovementType): string {
    const labels: Record<CustodyMovementType, string> = {
      checkout: 'Checked Out',
      checkin: 'Checked In',
      transfer: 'Custody Transfer',
      inter_mda_transfer: 'Inter-MDA Transfer',
      location_change: 'Location Changed',
      temporary_access: 'Temporary Access',
      retrieval: 'Retrieved from Archive',
      archival: 'Sent to Archive',
      disposal: 'Disposed',
    };
    return labels[type];
  }

  /**
   * Get movement type icon
   */
  getMovementTypeIcon(type: CustodyMovementType): string {
    const icons: Record<CustodyMovementType, string> = {
      checkout: '↗',
      checkin: '↙',
      transfer: '→',
      inter_mda_transfer: '⇄',
      location_change: '📍',
      temporary_access: '👁',
      retrieval: '📤',
      archival: '📦',
      disposal: '🗑',
    };
    return icons[type];
  }

  /**
   * Verify handover
   */
  verifyHandover(
    _recordId: string,
    method: VerificationMethod,
    signature?: string,
    witnessName?: string
  ): Partial<CustodyRecord> {
    return {
      verificationMethod: method,
      verifiedAt: new Date(),
      signature,
      witnessName,
    };
  }

  /**
   * Calculate custody risk score
   */
  calculateRiskScore(chain: CustodyChain): {
    score: number;
    level: 'low' | 'medium' | 'high' | 'critical';
    factors: string[];
  } {
    let score = 0;
    const factors: string[] = [];

    // Overdue checkout
    if (chain.isCheckedOut && chain.averageCustodyDuration > 30) {
      score += 30;
      factors.push('Extended checkout duration');
    }

    // Unresolved alerts
    const unresolvedAlerts = chain.alerts.filter(a => !a.isResolved);
    if (unresolvedAlerts.length > 0) {
      score += unresolvedAlerts.length * 10;
      factors.push(`${unresolvedAlerts.length} unresolved alerts`);
    }

    // Critical alerts
    const criticalAlerts = unresolvedAlerts.filter(a => a.severity === 'critical');
    if (criticalAlerts.length > 0) {
      score += criticalAlerts.length * 20;
      factors.push(`${criticalAlerts.length} critical alerts`);
    }

    // Missing verifications
    const unverified = chain.chain.filter(r => !r.verifiedAt);
    if (unverified.length > 0) {
      score += unverified.length * 5;
      factors.push(`${unverified.length} unverified movements`);
    }

    // Determine level
    let level: 'low' | 'medium' | 'high' | 'critical' = 'low';
    if (score >= 70) level = 'critical';
    else if (score >= 50) level = 'high';
    else if (score >= 25) level = 'medium';

    return { score: Math.min(100, score), level, factors };
  }
}

// Export singleton instance
export const custodyTracking = new CustodyTrackingService();

export type { CustodyTrackingService };
